import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';

/**
 * This component handles Supabase authentication callbacks
 * It catches password reset tokens and redirects to the reset-password page
 * Supports both legacy token-based and modern PKCE code-based flows
 */
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleAuthCallback = async () => {
      // Log everything for debugging
      console.log('🔍 AuthCallback Active');
      console.log('🔍 Current Path:', location.pathname);
      console.log('🔍 Hash:', location.hash);
      console.log('🔍 Search:', location.search);
      console.log('🔍 Full Href:', window.location.href);

      const hash = location.hash;
      const search = location.search;
      const params = new URLSearchParams(search);
      
      // Check for PKCE code parameter (Supabase's modern auth flow)
      const code = params.get('code');
      
      if (code) {
        console.log('🔐 Found PKCE code parameter:', code.substring(0, 20) + '...');
        
        try {
          // Try the newer method first
          let result;
          
          if (typeof supabase.auth.exchangeCodeForSession === 'function') {
            console.log('🔄 Using exchangeCodeForSession method...');
            result = await supabase.auth.exchangeCodeForSession(code);
          } else {
            console.log('🔄 Using getSession to trigger PKCE exchange...');
            // In some versions, just getting the session with the code in URL triggers exchange
            result = await supabase.auth.getSession();
          }
          
          const { data, error } = result;
          
          if (error) {
            console.error('❌ PKCE exchange error:', error);
            console.error('Error details:', JSON.stringify(error, null, 2));
            
            // Try alternative: just redirect and let ResetPassword handle it
            console.log('⚠️ Trying alternative: redirect with code parameter');
            navigate(`/reset-password?code=${code}`, { replace: true });
            return;
          }
          
          if (data?.session) {
            console.log('✅ Session obtained successfully!');
            console.log('✅ User:', data.session.user?.email);
            // Session is now active, redirect to reset password page
            navigate('/reset-password', { replace: true });
            return;
          } else {
            console.warn('⚠️ No session in response, redirecting with code');
            navigate(`/reset-password?code=${code}`, { replace: true });
            return;
          }
        } catch (err) {
          console.error('❌ Exception during code exchange:', err);
          console.error('Exception details:', err.message, err.stack);
          // Redirect with code so ResetPassword can try
          navigate(`/reset-password?code=${code}`, { replace: true });
          return;
        }
      }
      
      // Check hash parameters (legacy format: #access_token=...)
      if (hash && hash.includes('access_token')) {
        console.log('✅ Found access_token in hash, redirecting to reset-password');
        navigate(`/reset-password${hash}`, { replace: true });
        return;
      }

      // Check query parameters for other auth types
      const type = params.get('type');
      const token = params.get('token');
      const accessToken = params.get('access_token');
      const tokenHash = params.get('token_hash');

      console.log('📋 Query Params:', { 
        code: code ? 'present' : 'missing',
        type, 
        token: token ? 'present' : 'missing', 
        accessToken: accessToken ? 'present' : 'missing', 
        tokenHash: tokenHash ? 'present' : 'missing' 
      });

      if (type === 'recovery' || token || accessToken || tokenHash) {
        console.log('✅ Found recovery parameters, redirecting to reset-password');
        // Convert query params to hash format for consistency
        const newHash = `#${search.substring(1)}`;
        navigate(`/reset-password${newHash}`, { replace: true });
        return;
      }
    };

    handleAuthCallback();
  }, [navigate, location]);

  return null; // This component doesn't render anything
}

export default AuthCallback;
